/**
 * Product Details Hook
 *
 * Fetches product details from the yolo-shop API (via yolo-site-api).
 * Uses the local products.service instead of directly calling Axiz API.
 */

import { useQuery } from '@tanstack/react-query';
import { getProductDetails, ProductDetails } from '../services/products.service';

interface UseProductDetailsOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch product details by brand, product ID, and market.
 *
 * @param brand - Brand ID
 * @param productId - Product identifier
 * @param market - Market ID (defaults to 14 = South Africa)
 * @param options - Query options
 */
export function useProductDetails(
  brand?: number,
  productId?: string,
  market?: number,
  options: UseProductDetailsOptions = {},
) {
  const { enabled = true } = options;

  return useQuery<ProductDetails | null, Error>({
    queryKey: ['productDetails', brand, productId, market],
    queryFn: async ({ signal }) => {
      if (!brand || !productId) {
        return null;
      }
      return getProductDetails(brand, productId, market ?? 14, signal);
    },
    enabled: enabled && !!brand && !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404
      if ((error as { status?: number }).status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
